import { UnitConfig } from '../Config'
import { IdentifiableObject, School } from '../types'
import { LeasePet } from '../Lease/types'

export enum ProspectListSearchStatus {
    DRAFT = 'DRAFT',
    SEARCH_IN_PROGRESS = 'SEARCH_IN_PROGRESS',
    SEARCH_COMPLETED = 'SEARCH_COMPLETED',
    SEARCH_FAILED = 'SEARCH_FAILED',
    UPLOAD_IN_PROGRESS = 'UPLOAD_IN_PROGRESS',
    UPLOAD_FAILED = 'UPLOAD_FAILED',
    UPLOAD_COMPLETED = 'UPLOAD_COMPLETED',
}

export enum ProspectListEnrichmentStatus {
    NOT_STARTED = 'NOT_STARTED',
    PENDING = 'PENDING',
    COMPLETED = 'COMPLETED',
    PARTIALLY_COMPLETED = 'PARTIALLY_COMPLETED',
}

export interface EnrichedValue {
    value: string
    last_attempted: string
}

export interface Prospect {
    id: number
    name: string
    graduationYear?: number
    school?: PoolSchoolSummary
    email: EnrichedValue
    phone: EnrichedValue
    linkedinProfile: EnrichedValue
    active_campaign: Campaign | null
    /** Global opt-out from campaign marketing emails (one-click from email); null if still eligible */
    email_unsubscribed_at?: string | null
    custom_data?: { [key: string]: string | number | null }
}

export interface PoolSchoolSummary {
    id: number
    effective_display_name: string
}

export interface ServedSchoolProspectSummary {
    school_id: number
    effective_display_name: string
    is_primary: boolean
    inferred_distance_miles: number | null
    fall_enrollment_total: number | null
    city: string | null
    state: string | null
    /** Total pool rows for this school (targets + enriched). */
    prospect_count: number
    /** Pool rows without revealed contact info. */
    target_count: number
    /** Pool rows with any revealed contact info (product "prospect"). */
    enriched_count: number
}

export interface ApartmentProspectsSummaryResponse {
    schools: ServedSchoolProspectSummary[]
    total_prospect_count: number
    graduation_years: number[]
}

export interface ApartmentProspectsBrowseResponse {
    count: number
    next: string | null
    previous: string | null
    results: Prospect[]
    summary_stats?: ProspectListSummaryStats
}

export interface ProspectProviderSnapshot {
    provider: string
    payload: Record<string, unknown>
    content_hash: string
    emails_at_ingest: string[]
    payload_byte_size: number
    created_date: string
    modified_date: string
}

export enum ProspectListGenerationMethod {
    AI_POWERED = 'AI_POWERED',
    SPREADSHEET = 'SPREADSHEET',
}

export interface ProspectListDetailSummary {
    id: number
    name: string
    apartment: number
    created_by: number
    schools: School[]
    location: string
    /** Backend may serialize as `string`, `string[]`, or occasionally a number in JSON. */
    graduation_years: string[] | string | number
    prospect_limit: number
    created_date: string
    modified_date: string
    list_status: ProspectListSearchStatus
    enrichment_status: ProspectListEnrichmentStatus
    generation_method: ProspectListGenerationMethod
    total_prospects: number
    contactable_prospects: number
    in_campaign: number
    tokens_remaining: number
    /** Present on PATCH when the backend starts an async search task */
    task_id?: string | null
}

export interface ProspectListSummaryStats {
    /** Full list size; unchanged when API applies quick filters / search. */
    total_prospects_found: number
    /** Count matching current filters (same filtered universe as `count` on paginated response). */
    total_prospects: number
    in_campaign: number
    with_email: number
    with_phone: number
    with_profile: number
    email_attempted: number
    phone_attempted: number
    profile_url_attempted: number
    phone_eligible_count?: number
    phone_eligible_attempted?: number
    name: string
    enrichment_status: ProspectListEnrichmentStatus
    schools: School[]
    location: string
    prospect_limit: number
    /** Backend may serialize as `string`, `string[]`, or occasionally a number in JSON. */
    graduation_years: string[] | string | number
    created_date: string
    modified_date: string
    generation_method: ProspectListGenerationMethod
    list_status: ProspectListSearchStatus
    tokens_remaining: number
}

export interface PaginatedProspectListResponse {
    count: number
    next: string | null
    previous: string | null
    results: Prospect[]
    summary_stats: ProspectListSummaryStats
}

export interface ProspectList {
    count: number
    next: string | null
    previous: string | null
    results: Prospect[]
    summary_stats: ProspectListSummaryStats
}

// Type for Create/Update operations (uses the old structure without pagination)
export interface ProspectListInput {
    name?: string
    schools?: School[]
    location?: string
    graduation_years?: string[]
    /** Server enrichment automation ids on this prospect list (`EnrichmentAutomation.id`). */
    enrichment_automation_ids?: number[]
    generation_method?: ProspectListGenerationMethod
    list_status?: ProspectListSearchStatus
}

export enum ProviderBillingMode {
    PER_RESULT = 'per_result',
    PER_SEARCH = 'per_search',
}

export enum CampaignStepType {
    EMAIL = 'EMAIL',
    TEXT_MESSAGE = 'TEXT_MESSAGE',
    DELAY = 'DELAY',
}

export interface CampaignStepSchedule extends IdentifiableObject {
    step: number
    day_of_week: number
    day_of_week_display: string
    enabled: boolean
    start_time: string
    end_time: string
    created_date: string
    modified_date: string
}

export interface CampaignStepAttachment extends IdentifiableObject {
    step: number
    file_name: string
    file_type: string
    file_size: number
    s3_key: string
    created_date: string
    modified_date: string
}

export interface CampaignStep extends IdentifiableObject {
    campaign: number
    step_order: number
    step_type: CampaignStepType
    subject: string | null
    content: string
    content_html: string | null
    delay_days: number | null
    created_date: string
    modified_date: string
    schedule: CampaignStepSchedule[]
    guest_card: GuestCard | null
    attachments: CampaignStepAttachment[]
    total_sent: number
    total_opens: number
    total_clicks: number
    total_converted: number
}

export enum CampaignStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    PAUSED = 'PAUSED',
    ARCHIVED = 'ARCHIVED',
}

export enum CampaignSystemKind {
    PRIMARY_EMAIL = 'primary_email',
    PRIMARY_TEXT = 'primary_text',
}

export interface Campaign extends IdentifiableObject {
    name: string
    apartment: number
    created_by: number
    active: boolean
    campaign_goal: string
    created_date: string
    modified_date: string
    steps: CampaignStep[]
    status: CampaignStatus
    reply_to_email: string
    sender_name: string
    concessions: number[]
    concession_names: string[]
    total_sent: number
    total_opens: number
    total_clicks: number
    total_prospects: number
    total_converted: number
    leasing_season?: number | null
    system_kind?: CampaignSystemKind | null
    pending_prospects?: number
    setup_complete?: boolean
}

export interface Provider {
    id: number
    name: string
    credit_cost: number
    billing_mode: ProviderBillingMode
}

export interface ProspectEnrichmentOutputCost {
    id: number
    output_field: string
    token_cost: number
    created_date: string
    modified_date: string
}

export type BulkEnrichField = 'email' | 'phone' | 'linkedinProfile'

export type BulkEnrichmentMode = 'NEVER_ATTEMPTED' | 'MISSING' | 'ALL'

/** GET /outreach/prospect-lists/{id}/bulk-enrich-preview/?field= */
export interface BulkEnrichPreviewResponse {
    field: BulkEnrichField
    token_cost_per_prospect: number
    counts: Record<BulkEnrichmentMode, number>
    estimated_tokens: Record<BulkEnrichmentMode, number>
}

/** Row from GET `/outreach/school-pool-stats/` (admin account managers). */
export interface SchoolPoolStatsRow {
    id: number
    display_name: string
    fall_enrollment_total: number | null
    degrees_awarded_highest: number | null
    total_prospects: number
    searched_prospects: number
    local_prospects: number
    last_search_date: string | null
    num_properties_served: number
    num_properties_primary: number
    num_lists: number
    num_workspaces: number
    /** Served by a property with an unarchived leasing season (scheduled target search). */
    in_pool_sweep: boolean
    /** A Fetch 100 or AMAP Celery task is queued or running for this school. */
    pool_search_in_flight: boolean
}

/** Nested address on admin school rows (`GET/PATCH /outreach/schools/`). */
export interface AdminSchoolAddress {
    id?: number
    address1?: string
    address2?: string | null
    city?: string
    state?: string
    zip_code?: string
    country?: string
    longitude?: number
    latitude?: number
    verified?: boolean
    created_date?: string
    modified_date?: string
}

/** Row from `SchoolAdminSerializer` (`GET /outreach/schools/`). */
export interface AdminSchoolRow {
    id: number
    name: string
    address: AdminSchoolAddress
    ipeds_id: string
    website_url?: string | null
    created_date?: string
    modified_date?: string
    google_place_id?: string
    google_places_display_name?: string
    ezos_admin_display_name_override?: string
    effective_display_name: string
    outreach_eligible: boolean
    fall_enrollment_total?: number | null
}

/** Writable body for `PUT/PATCH /outreach/schools/{id}/` (only override is applied). */
export interface AdminSchoolWrite {
    ezos_admin_display_name_override?: string
}

export enum CampaignProspectStatus {
    PENDING = 'PENDING',
    ACTIVE = 'ACTIVE',
    COMPLETED = 'COMPLETED',
    STOPPED = 'STOPPED',
    REPLIED = 'REPLIED',
}

/** Nested prospect returned on campaign enrollment list endpoints */
export interface CampaignEnrollmentProspect {
    id: number
    name: string
    created_date: string
    modified_date: string
    email_unsubscribed_at?: string | null
}

export interface CampaignProspect extends IdentifiableObject {
    campaign: number
    prospect: CampaignEnrollmentProspect
    current_step: CampaignStep
    status: CampaignProspectStatus
    status_display: string
    started_at: string
    last_step_sent_at: string | null
    replied_at: string | null
    next_step_scheduled_at: string
    messages_sent: number
    messages_opened: number
    messages_clicked: number
    concessions: number[]
    concession_names: string[]
    created_date: string
    modified_date: string
}

export interface CampaignProspectActivity {
    type: string // e.g., "email_opened", "email_sent", "email_clicked"
    identifier: string
    action: string
    timestamp: string
    relative_time: string
    prospect_id?: number
    step_order?: number
    count?: number // For sent activities
}

export enum CampaignProspectActivityType {
    SENT = 'SENT',
    OPENED = 'OPENED',
    CLICKED = 'CLICKED',
}

export interface GuestCardCustomQuestionOption extends IdentifiableObject {
    label: string
    order: number
    created_date: string
    modified_date: string
}

export type GuestCardQuestionType =
    | 'SHORT_TEXT'
    | 'LONG_TEXT'
    | 'DROPDOWN'
    | 'MULTIPLE_CHOICE'
    | 'YES_NO'
    | 'DATE'
    | 'NUMBER'

export interface GuestCardCustomQuestion extends IdentifiableObject {
    apartment: number
    question: string
    question_type: GuestCardQuestionType
    options: GuestCardCustomQuestionOption[]
    archived: boolean
    created_date: string
    modified_date: string
}

export interface GuestCardQuestion extends IdentifiableObject {
    custom_question: GuestCardCustomQuestion
    order: number
    required: boolean
    created_date: string
    modified_date: string
}

export interface GuestCardPremiumOption {
    id: number
    name: string
    description?: string
    amount: string
}

export interface GuestCardLinkUnitConfig {
    id: number
    name: string
}

export interface GuestCardLink extends IdentifiableObject {
    guest_card: number
    unit_config: GuestCardLinkUnitConfig | null
    token: string
    public_url: string
    label: string
    active: boolean
    created_date: string
    modified_date: string
}

export interface GuestCardRequiredFields {
    phone?: boolean
    preferred_unit_config?: boolean
    preferred_premiums?: boolean
    preferred_rent_cost?: boolean
    preferred_move_in_date?: boolean
    preferred_move_out_date?: boolean
    preferred_roommates?: boolean
    school_attendance?: boolean
    additional_requests?: boolean
}

export interface GuestCard extends IdentifiableObject {
    card_name: string
    archived: boolean
    default: boolean
    apartment: number
    header: string
    body: string
    name: boolean
    email: boolean
    phone: boolean
    preferred_unit_config: boolean
    preferred_premiums: boolean
    preferred_rent_cost: boolean
    preferred_move_in_date: boolean
    preferred_move_out_date: boolean
    preferred_roommates: boolean
    school_attendance: boolean
    additional_requests: boolean
    required_fields?: GuestCardRequiredFields
    default_move_in_date?: string | null
    default_move_out_date?: string | null
    card_questions: GuestCardQuestion[]
    links?: GuestCardLink[]
    created_date: string
    modified_date: string
}

export enum LeadSource {
    EMAIL = 'email',
    PHONE = 'phone',
    PROPERTY = 'property',
    WEBSITE = 'website',
    LEADPIPE = 'leadpipe',
}

export interface DetailedCampaignProspect extends IdentifiableObject {
    campaign: Campaign
    prospect: Prospect
    current_step: CampaignStep
    status: CampaignProspectStatus
    status_display: string
    started_at: string
    last_step_sent_at: string
    replied_at: string | null
    next_step_scheduled_at: string
    messages_sent: number
    messages_opened: number
    messages_clicked: number
    created_date: string
    modified_date: string
}

export enum LeadSyncStatus {
    NOT_SYNCED = 'NOT_SYNCED',
    SYNC_PENDING = 'SYNC_PENDING',
    SYNC_FAILED = 'SYNC_FAILED',
    SYNCED = 'SYNCED',
}

export enum LeadSyncStatusDisplay {
    NOT_SYNCED = 'Not Synced',
    SYNC_PENDING = 'Sync Pending',
    SYNC_FAILED = 'Sync Failed',
    SYNCED = 'Synced',
}

export interface GuestCardCustomQuestionAnswer extends IdentifiableObject {
    answer: string
    created_date: string
    modified_date: string
    guest_card_custom_question: number
}

export interface PreferredRommmate extends IdentifiableObject {
    name: string
    email: string | null
    phone: string | null
    created_date: string
    modified_date: string
}

export interface LeadApplicationSyncChange {
    field: string
    target: string
    old: string | null
    new: string | null
    status: string
    source: string
    collected_at: string
    reason?: string
    kind?: string
}

export interface LeadApplicationSigning {
    id: number
    status: string
    sent_at: string | null
    started_at: string | null
    completed_at: string | null
    signing_progress: number | null
    application_fee_cents: number
    submission_snapshot: Record<string, string> | null
    synced_field_changes: LeadApplicationSyncChange[]
    sync_conflicts: LeadApplicationSyncChange[]
    pending_review: boolean
    signable_doc_id: number
    signable_doc_name: string
    signed_application_id: number | null
    signed_pdf_download_url: string | null
}

export type LeasePackageStatus = 'sent' | 'awaiting_property' | 'signed'

export interface LeadLeasePackagePropertyUrl {
    signable_doc_id: string
    signable_doc_name: string
    signing_url: string
}

export interface LeadLeasePackage {
    status: LeasePackageStatus
    sent_at: string | null
    signed_at: string | null
    progress: number
    missing_signatures: string[]
    awaiting_property: boolean
    property_signing_urls: LeadLeasePackagePropertyUrl[]
    documents: Array<{
        signing_request_id: number
        signable_doc_id: number
        signable_doc_name: string
        document_type: string
        document_type_label?: string
        status: string
        sent_at: string | null
        completed_at: string | null
        progress: number
        missing_signatures: string[]
        signed_pdf_url?: string | null
    }>
}

export type ApplicationSubStatus = 'sent' | 'submitted'

export type ScreeningRequestStatus =
    | 'PENDING'
    | 'INVITED'
    | 'PROCESSING'
    | 'COMPLETE'
    | 'FAILED'
    | 'EXPIRED'

export type ScreeningResult =
    | 'pending'
    | 'approved'
    | 'approved_prior_resident'
    | 'conditional'
    | 'declined'

export type BackgroundCheckStatus = 'pending' | 'clear' | 'consider'

export interface LeadScreeningRequest {
    id: number
    provider: 'checkr' | 'singlekey' | string
    status: ScreeningRequestStatus
    package: string
    invitation_url: string
    report_status: 'clear' | 'consider' | null
    checkr_candidate_id: string
    checkr_invitation_id: string
    checkr_report_id: string
    invited_at: string | null
    completed_at: string | null
    last_error: string | null
}

export interface LeadApplicationFeePaymentSummary {
    amount: string
    paid_at: string | null
    status: string
}

export type ApplicationFeePaymentStatus =
    | 'pending'
    | 'succeeded'
    | 'failed'
    | 'refunded'

export interface ApplicationFeePaymentListItem {
    id: number
    lead_id: number
    lead_name: string
    amount: string
    paid_at: string | null
    status: ApplicationFeePaymentStatus
    signing_request_id: number
    leasing_season_id: number
    leasing_season_name: string
}

export interface PaginatedApplicationFeePaymentsResponse {
    count: number
    next: string | null
    previous: string | null
    results: ApplicationFeePaymentListItem[]
}

export type LeadPipelineStage =
    | 'leads'
    | 'tours'
    | 'app_sent'
    | 'app_signed_pending_review'
    | 'screening'
    | 'lease_sent'
    | 'lease_signed'
    | 'guarantor'
    | 'future_resident'

export type LeadTourStatus = 'scheduled' | 'completed' | 'no_show' | 'skipped'

export interface LeadTourUser {
    id: number
    name: string
}

export interface LeadTour {
    id: number
    lead: number
    status: LeadTourStatus
    starts_at: string | null
    ends_at: string | null
    given_by: LeadTourUser | null
    scheduled_by: LeadTourUser | null
    notes: string
    completed_at: string | null
    created_date: string
    modified_date: string
}

export interface ApartmentTourGuestCardCustomAnswer {
    question_id: number
    question: string
    answer: string
}

export interface ApartmentTourGuestCardResults {
    preferred_unit_config: { id: number; name: string } | null
    preferred_rent_cost: number | null
    preferred_move_in_date: string | null
    additional_requests: string | null
    custom_answers: ApartmentTourGuestCardCustomAnswer[]
}

/** Nested lead context from GET /outreach/tours/ (apartment tour list) */
export interface ApartmentTourLead {
    id: number
    pipeline_stage: string
    prospect_id: number
    name: string
    email: string | null
    phone: string | null
    guest_card_completed: boolean
    guest_card_results: ApartmentTourGuestCardResults | null
}

/** Flat tour from GET /outreach/tours/ (property-scoped list) */
export interface ApartmentTour {
    id: number
    status: LeadTourStatus
    starts_at: string | null
    ends_at: string | null
    given_by: LeadTourUser | null
    scheduled_by: LeadTourUser | null
    notes: string
    completed_at: string | null
    created_date: string
    modified_date: string
    lead: ApartmentTourLead
}

export interface Lead {
    id: number
    guest_card: GuestCard
    preferred_unit_config: UnitConfig | null
    preferred_premiums?: Array<{
        id: number
        name: string
        amount: string
    }>
    preferred_rent_cost: number | null
    preferred_move_in_date: string | null
    preferred_move_out_date: string | null
    school_attendance?: number | null
    school_attendance_name: string | null
    additional_requests: string | null
    lead_source: LeadSource
    pipeline_stage: LeadPipelineStage
    pipeline_step_id?: string | null
    application_signing: LeadApplicationSigning | null
    lease_package?: LeadLeasePackage | null
    lease_packet_preview?: Array<{
        id?: string | null
        doc_id: number | null
        name: string | null
        document_type: string | null
        included: boolean
        rule_type: 'always' | 'when_answer' | 'when_qualifying_method' | string
    }>
    application_status: ApplicationSubStatus | null
    application_fee_cents?: number | null
    application_fee_payment: LeadApplicationFeePaymentSummary | null
    qualifying_method?: string | null
    workbook_sections_complete?: number | null
    workbook_sections_total?: number | null
    workbook_last_activity_at?: string | null
    has_submission_pdf?: boolean
    screening_result?: ScreeningResult
    background_check?: BackgroundCheckStatus
    credit_score?: number | null
    screening_report_url?: string
    screening_request?: LeadScreeningRequest | null
    current_tour?: LeadTour | null
    tours?: LeadTour[]
    user_id?: number | null
    account_status?: 'none' | 'invited' | 'active'
    invitation_sent_at?: string | null
    account_activated_at?: string | null
    pets?: LeasePet[]
    prospect_detail?: {
        id: number
        name: string
        email: string | null
        phone: string | null
        created_date: string
        modified_date: string
    } | null
    identity?: {
        name: string
        email: string
        phone: string
        source: 'application' | 'prospect' | string
    }
    waiting_on?: {
        waiting_on: 'them' | 'us' | 'you' | null
        label: string | null
        detail: string | null
        waiting_since?: string | null
    }
    guarantor?: {
        name: string
        email: string
        method: string
    } | null
    created_date: string
    modified_date: string
    campaign_prospect: DetailedCampaignProspect | null
    sync_status: LeadSyncStatus
    entrata_id: number | null
    last_sync_attempted_at: string | null
    guest_card_completed: boolean
    custom_answers: GuestCardCustomQuestionAnswer[]
    preferred_roommates: PreferredRommmate[]
}

export interface LeadPipePixel {
    id: number
    apartment: number
    pixel_id: string
    domain: string
    name: string
    status: 'ACTIVE' | 'PAUSED'
    code: string
    created_by: number
    created_date: string
    modified_date: string
}

export type SignableDocType =
    | 'APPLICATION'
    | 'SELF_GUARANTOR'
    | 'GUARANTOR_APPLICATION'
    | 'GUARANTOR_AGREEMENT'
    | 'LEASE'
export type SignableDocStatus = 'DRAFT' | 'READY' | 'ARCHIVED'

export const SIGNABLE_DOC_TYPE_LABELS: Record<SignableDocType, string> = {
    APPLICATION: 'Application (legacy)',
    SELF_GUARANTOR: 'Self guarantor / addendum',
    GUARANTOR_APPLICATION: 'Guarantor application',
    GUARANTOR_AGREEMENT: 'Guarantor agreement',
    LEASE: 'Lease / lease addendum',
}

export const SIGNABLE_DOC_TYPES: SignableDocType[] = [
    'APPLICATION',
    'SELF_GUARANTOR',
    'GUARANTOR_APPLICATION',
    'GUARANTOR_AGREEMENT',
    'LEASE',
]

export interface SignableDocAssignedSeason {
    id: number
    name: string
}

export interface SignableDocFieldMapping {
    kind?: string
    source?: string
    fields?: string[]
}

export interface SignableDocValueSource {
    path: string
    label: string
    category: string
    category_label: string
    description: string
}

export interface SignableDocApplicantDataField {
    field_id: string
    question_text: string
    input_type: string
    section_id: string
    section_label: string
}

export type SignableDocPropertyDataField = SignableDocApplicantDataField

export interface SignableDocMappingKind {
    id: string
    label: string
}

export interface SignableDocTemplateField {
    name: string
    type: string
    role: string
    suggested_source: string
    suggested_kind?: string
    suggested_fields?: string[]
}

export interface SignableDoc {
    id: number
    name: string
    document_type: SignableDocType
    status: SignableDocStatus
    unit_config: {
        id: number
        name: string
        bed_count: number | null
        bath_count: number | null
        shows_on_schedule: boolean
    } | null
    docuseal_template_id: number | null
    docuseal_slug: string | null
    copied_from: number | null
    catalog_template_key?: string | null
    doc_role?: string | null
    source?: string | null
    created_from_template_version?: number | null
    created_from_org_doc?: number | null
    company_document_id?: number | null
    assigned_seasons: SignableDocAssignedSeason[]
    enabled_fields: string[]
    /** DocuSeal field name → EZOS value source path. */
    field_mappings?: Record<string, SignableDocFieldMapping>
    created_date: string
    modified_date: string
}

export interface SignableDocSummary {
    id: number
    name: string
    document_type: SignableDocType
    status: SignableDocStatus
    docuseal_template_id: number | null
    docuseal_slug: string | null
    unit_config_id: number | null
}

export interface SignableDocPreviewConfig {
    token: string
    host: string
    slug: string | null
    preview_values: Record<string, string>
}

export interface SignableDocBuilderToken {
    token: string
    host: string
    /** Set when the backend created/updated a DocuSeal template from the uploaded PDF. */
    template_id?: number
    template_slug?: string
}

export interface SignableDocCopyResult extends SignableDoc {
    source_document_urls: string[]
}
